import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import Slider from 'react-slick';
import classNames from 'classnames';
import isEmpty from 'lodash/isEmpty';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faYoutube as fabFaYoutube } from '@fortawesome/free-brands-svg-icons';

import VideoImgLoader from 'Components/Utility/Video/VideoImgLoader';
import FluidImg from 'Components/Utility/FluidImg';
import ProductCover from 'Components/Utility/ProductCover';
import AgeGatingContainer from 'Components/Product/AgeGatingContainer';
import pauseYTVideos from 'Helpers/Video/VideoHelpers';
import CarouselArrow from 'Components/Carousel/CarouselArrow';

const GameSlickCarousel = ({
  videos,
  ratings,
  bundleCover,
  screenshots,
  type,
  displayType,
  singleSlideCarousel,
  resetScreenshots,
  setResetScreenshots,
}) => {
  const [interacted, setInteracted] = useState(false);
  const bottomTrackRef = useRef();
  const topCarouselRef = useRef();

  /**
   * Set screenshot slider index to 0. when game in parent changes
  */
  useEffect(() => {
    if (resetScreenshots) {
      if (topCarouselRef?.current) topCarouselRef.current.slickGoTo(0);
      if (bottomTrackRef?.current) bottomTrackRef.current.slickGoTo(0);
      setResetScreenshots(false);
    }
  }, [resetScreenshots, setResetScreenshots]);

  const slides = videos.map(video => ({ video: true, src: video.src, cover: video.cover }));

  let trackSlidesToShow = 5;
  let slidesCount = bundleCover && isEmpty(videos) ? 1 : 0;

  slidesCount += slides.length + screenshots.length;

  trackSlidesToShow = Math.min(trackSlidesToShow, slidesCount);

  const bottomTrackSettings = {
    accessibility: true,
    slidesToShow: trackSlidesToShow,
    slidesToScroll: 1,
    infinite: true,
    dots: false,
    centerMode: false,
    focusOnSelect: true,
    beforeChange: (i, n) => {
      if (topCarouselRef?.current) topCarouselRef.current.slickGoTo(n);
    },
    arrows: true,
    prevArrow: <CarouselArrow ariaLabel="Previous" arrowDirection="left" containerClassName="slider-button arrow-left" onClick={pauseYTVideos} />,
    nextArrow: <CarouselArrow ariaLabel="Next" arrowDirection="right" containerClassName="slider-button arrow-right" onClick={pauseYTVideos} />,
    lazyLoad: interacted ? null : 'ondemand',
    responsive: [
      {
        breakpoint: 768,
        settings: {
          centerPadding: '0',
          variableWidth: true,
          centerMode: true,
        },
      },
    ],
  };

  const topCarouselSettings = {
    centerMode: false,
    accessibility: true,
    centerPadding: '0px',
    infinite: true,
    slidesToShow: 1,
    dots: false,
    arrows: true,
    draggable: true,
    autoplay: false,
    prevArrow: <CarouselArrow ariaLabel="Previous" arrowDirection="left" containerClassName="slider-button arrow-left" onClick={pauseYTVideos} />,
    nextArrow: <CarouselArrow ariaLabel="Next" arrowDirection="right" containerClassName="slider-button arrow-right" onClick={pauseYTVideos} />,
    lazyLoad: interacted ? null : 'ondemand',
    autoplaySpeed: 7500,
    speed: 500,
    variableWidth: false,
    dotsClass: 'slick-dots slick-thumb',
    afterChange: (i) => {
      if (bottomTrackRef?.current) bottomTrackRef.current.slickGoTo(i);

      if (!interacted) setInteracted(true);
    },
    responsive: [
      {
        breakpoint: 768,
        settings: {
          centerPadding: '0',
          variableWidth: true,
          centerMode: true,
        },
      },
    ],
  };
  // carousel that appears on the bottom ( track ) of the screen is actually first element in this component
  return (
    <AgeGatingContainer ratings={ratings}>
      <div className="d-flex flex-column-reverse">
        {!singleSlideCarousel &&
          <div className="d-none d-md-block game-slick-carousel-cover">
            <Slider
              ref={bottomTrackRef}
              {...bottomTrackSettings}
              className={
                classNames({ 'bundle-carousel-spacing': type === 'bundle' },
                  'product-carousel-track')
              }
            >
              {bundleCover && isEmpty(videos) &&
                <button aria-label="View image" className="slide-img-container remove-style w-100" onClick={pauseYTVideos}>
                  <ProductCover
                    thumbnail
                    cover={bundleCover}
                    alt={`Product cover for ${bundleCover}`}
                    displayType={displayType}
                  />
                </button>
              }
              {slides.map(({ video, slug, alt, src, cover }, i) =>
                <div key={`${slug}-${src}`}>
                  {video &&
                    <button aria-label="Play video" className="slide-img-container remove-style w-100" onClick={pauseYTVideos}>
                      <div className="video-thumb-container">
                        <div className="play-icon-container">
                          <FontAwesomeIcon icon={fabFaYoutube} />
                        </div>
                        {(type === 'bundle' && cover && i === 0) ?
                          <FluidImg ratio16by9 className="img-force-full" src={cover} alt={alt || cover} />
                          :
                          <FluidImg ratio16by9 className="img-force-full" youtubeId={src} alt={alt} />
                        }
                      </div>
                    </button>
                  }
                </div>,
              )}
              {screenshots && screenshots.map(({ slug, alt }) =>
                <button aria-label="View image" className="slide-img-container remove-style" key={slug} onClick={pauseYTVideos}>
                  <FluidImg ratio16by9 className="img-force-full" src={slug} alt={alt} />
                </button>,
              )}
            </Slider>
          </div>
        }

        <div>
          <Slider ref={topCarouselRef} {...topCarouselSettings} className="product-carousel" >
            {bundleCover && isEmpty(videos) &&
              <ProductCover
                cover={bundleCover}
                alt={`Product cover for ${bundleCover}`}
                displayType={displayType}
                sizesOverride="(min-width: 1520px) 662px, (min-width: 1200px) 562px, (min-width: 991px) 472px, (min-width: 768px) 704px, (min-width: 576px) 524px, 98vw"
              />
            }
            {slides.map(({ video, slug, src, cover }, i) =>
              <div key={`${slug}-${src}`}>
                {video && type === 'bundle' &&
                  <VideoImgLoader
                    cover={i === 0 ? cover : null}
                    video={src}
                    iconSize="4x"
                    sizesOverride="(min-width: 1520px) 662px, (min-width: 1200px) 562px, (min-width: 991px) 472px, (min-width: 768px) 704px, (min-width: 576px) 524px, 98vw"
                    location={'gameSlickCarousel'}
                    displayType={displayType}
                  />
                }
              </div>,
            )}
            {screenshots && screenshots.map(({ slug, alt }) =>
              <div key={slug}>
                <div className="responsive-image-island">
                  <div className="responsive-image responsive-image--16by9">
                    <FluidImg
                      ratio16by9
                      className="img-force-full"
                      src={slug}
                      alt={alt}
                      sizesOverride="(min-width: 1520px) 665px, (min-width: 1200px) 570px, (min-width: 992px) 480px, (min-width: 768px) 710px, (min-width: 576px) 530px, 95vw"
                    />
                  </div>
                </div>
              </div>,
            )}
          </Slider>
        </div>
      </div>
    </AgeGatingContainer>
  );
};

GameSlickCarousel.propTypes = {
  screenshots: PropTypes.arrayOf(PropTypes.object),
  videos: PropTypes.arrayOf(PropTypes.object),
  ratings: PropTypes.shape({}),
  bundleCover: PropTypes.string,
  type: PropTypes.string,
  displayType: PropTypes.string,
  singleSlideCarousel: PropTypes.bool,
  resetScreenshots: PropTypes.bool,
  setResetScreenshots: PropTypes.func,
};

GameSlickCarousel.defaultProps = {
  videos: [],
  ratings: {},
  bundleCover: '',
  screenshots: [],
  type: '',
  displayType: '',
  singleSlideCarousel: false,
  resetScreenshots: false,
  setResetScreenshots: () => {},
};

export default GameSlickCarousel;
